import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { has, isEmpty } from 'underscore';

import { shouldReloadCharts } from 'redux/selectors/analytics';
import { useAppDispatch } from 'redux/store';
import { surveyCharts } from 'redux/actions/analytics';

import useScript from 'shared/hooks/use-script';
import useIsMounted from 'shared/hooks/use-is-mounted';
import { useSurveyPanelsInitializer } from 'analytics_dashboard/hooks/use-initialize-qrvey';

import { config } from '@config/config.json';
import QrveyComponent from '../qrvey-component';
import QrveyPanels from '../qrvey-panels';
import { AnalyticsContext } from '../analytics-dashboard';
import AnalyticsLoader from '../analytics-loader';

const SurveyCharts: React.FC = () => {
  const { institutionId, catalogId,
    prePostSurveyConfigurationId: configurationId,
  } = useContext(AnalyticsContext);
  const shouldReload = useSelector(state => shouldReloadCharts(state));

  const [surveyChartsData, setSurveyChartsData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const isMounted = useIsMounted();
  const hasFetchedRef = useRef(false);

  const dispatch = useAppDispatch();

  const widgetStatus = useScript(config.qrvey.widgetUrl);

  const isReady = surveyChartsData && widgetStatus === 'ready';
  const { panels, isError } = useSurveyPanelsInitializer(surveyChartsData, isReady, isLoading);

  const getSurveyCharts = useCallback(async () => {
    if (configurationId) {
      setIsLoading(true);
      try {
        const result = await dispatch(surveyCharts({
          institutionId,
          preCourseCatalogId: catalogId,
          configurationId,
        }));
        if (isMounted() && result?.payload && !isEmpty(result.payload)) {
          setSurveyChartsData(result.payload);
        }
      } finally {
        if (isMounted()) {
          setIsLoading(false);
        }
      }
    }
  }, [configurationId, catalogId, dispatch, institutionId, isMounted]);

  useEffect(() => {
    // Load charts on mount and when dependencies of getSurveyCharts change
    if (!hasFetchedRef.current) {
      // Only fetch once on mount
      hasFetchedRef.current = true;
      getSurveyCharts();
    }
  }, [getSurveyCharts]);

  useEffect(() => {
    if (shouldReload) {
      getSurveyCharts();
    }
  }, [getSurveyCharts, shouldReload]);

  if (!surveyChartsData || shouldReload) {
    return <AnalyticsLoader />;
  }

  return (
    <QrveyComponent
      panels={panels?.length > 0 ? <QrveyPanels panels={panels} /> : null}
      isReady={isReady && !isError}
      showdashboard={false}
    />
  );
};

export default SurveyCharts;
